import * as React from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FormPageLayout } from '../../../../../components/layout/FormPageLayout';
import { Card, CardContent } from '../../../../../components/ui/Card';
import { Button } from '../../../../../components/ui/Button';
import { Input, FormField } from '../../../../../components/ui/FormField';
import { FormSelect } from '../../../../../components/ui/FormSelect';
import { useToast } from '../../../../../components/ui/Toast';
import { Tablet } from 'lucide-react';
import { useDevices, useDispatchDevice } from '../../../../../features/settings/network/devices.api';
import { agentHooks } from '../../../../../features/agents/hooks';
import { usePageTitle } from '../../../../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Banner } from '../../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/settings/devices/$id/dispatch')({
  component: DispatchDevicePage,
});

const dispatchFormSchema = z.object({
  agentId: z.string().min(1, 'Agent is required'),
  remarks: z.string().optional(),
});

type FormValues = z.infer<typeof dispatchFormSchema>;

const dispatchFormConfig = defineFormPage<FormValues>({
  resource: 'devices',
  defaultValues: {
    agentId: '',
    remarks: '',
  },
  schema: dispatchFormSchema,
});

function DispatchDevicePage() {
  usePageTitle('Issue Device');
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { toast } = useToast();
  const { data: devicesData = [] } = useDevices();
  const selectedDevice = devicesData.find((d) => d.id === id);

  const { data: agentsRes } = agentHooks.useList();
  const agents = agentsRes?.data || [];

  const dispatchDeviceMutation = useDispatchDevice();

  const {
    form,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(dispatchFormConfig, {
    mutateAsync: (values) => dispatchDeviceMutation.mutateAsync({ id, agentId: values.agentId, remarks: values.remarks })
  }, {
    routeId: `settings/devices/${id}/dispatch`,
  });

  const onFormSubmit = async (e: React.FormEvent) => {
    await handleSubmit(e);
    if (!selectedDevice) return;
    clearDraft();
    toast({
      type: 'success',
      title: 'Device Issued',
      description: 'Tablet successfully assigned and locked to the agent.',
    });
    void navigate({ to: '/settings/devices' });
  };

  return (
    <FormPageLayout
      title="Issue Device Dispatch"
      subtitle="Assign the device to an agent and log standard morning dispatch notes."
      backTo="/settings/devices"
    >
      <Card className="shadow-sm max-w-xl mx-auto">
        <CardContent className="p-6">
          <form onSubmit={onFormSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <div className="bg-surface-2 p-3 rounded-lg border border-border flex items-center gap-2 mb-4">
              <Tablet className="w-5 h-5 text-accent-500 shrink-0" />
              <div>
                <p className="text-xs font-bold text-text-primary">{selectedDevice?.label || 'Loading device...'}</p>
                <p className="text-caption font-mono text-text-muted mt-0.5">{selectedDevice?.fingerprint}</p>
              </div>
            </div>

            <form.Field
              name="agentId"
              children={(field: AnyFieldApi) => (
                <FormSelect
                  label="Assign to Field Agent"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  options={agents.map((agent: { id: string; fullName: string; agentNumber: string }) => ({
                    value: agent.id,
                    label: `${agent.fullName} (${agent.agentNumber})`,
                  }))}
                  error={fe(field.state.meta)}
                />
              )}
            />

            <form.Field
              name="remarks"
              children={(field: AnyFieldApi) => (
                <FormField label="Dispatch / Field Route Notes">
                  <Input
                    placeholder="e.g. Route 4 collection - West Sector"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </FormField>
              )}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="secondary"
                onClick={() => void navigate({ to: '/settings/devices' })}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!selectedDevice}>
                Log Dispatch
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </FormPageLayout>
  );
}
